import React, { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { Button as AntButton, Space, Tabs as AntTabs } from 'antd';
import type { TabsProps } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { emphasize, styled } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../../../Styles/StyleHome.css';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import MfaAdminPanel from './MfaAdminPanel';

export type MfaActionMode = 'view' | 'add' | 'edit';

interface MfaPageProps { isAdmin?: boolean; }

// Administration MFA regroupe désormais TOUS les niveaux de politique
// (sécurité globale, société, agence, rôle, utilisateur) dans un seul
// panneau avec sélecteur de niveau interne. L'onglet "Société" est désormais
// une page autonome accessible via la route /societe.
const TABS = [
  { label: 'Administration MFA', supportsAdd: true, supportsEdit: true, supportsDelete: false },
] as const;

// Fil d'Ariane conforme à la charte (voir ClientPhysique.tsx)
const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
}) as typeof Chip;

export default function MfaPage({ isAdmin }: MfaPageProps) {
  const auth = useAuthUser();
  const currentUser: any = auth();
  const effectiveIsAdmin = isAdmin ?? (
    currentUser?.Role === '999' ||
    currentUser?.role === '999' ||
    currentUser?.groupCode === '999' ||
    currentUser?.codgroup === '999'
  );

  const [tab, setTab] = useState(0);
  const [actionMode, setActionMode] = useState<MfaActionMode>('view');
  const [saveRequest, setSaveRequest] = useState(0);
  const [deleteRequest, setDeleteRequest] = useState(0);

  const isEditing = actionMode !== 'view';
  const resetAction = () => setActionMode('view');
  const editSelection = () => setActionMode('edit');

  const currentTab = TABS[tab];
  const hasSelectionToEdit = true;

  // if (!effectiveIsAdmin) {
  //   return (
  //     <div className="tab">
  //       <Alert severity="info" sx={{ mt: 2 }}>
  //         L'accès à la gestion MFA est réservé à l'administrateur.
  //       </Alert>
  //     </div>
  //   ); 
  // }

  const items: TabsProps['items'] = [
    {
      key: '0',
      label: (
        <span>
          <SafetyCertificateOutlined />
          Administration MFA 
        </span>
      ),
      children: tab === 0 ? (
        <MfaAdminPanel mode={actionMode} saveRequest={saveRequest} onSaved={resetAction} />
      ) : null,
    },
  ];

  return (
    <div className="tab">
      <Box className="formTitle">
        <Breadcrumbs aria-label="breadcrumb">
          <StyledBreadcrumb
            component="a"
            href="/jjh"
            label="Home"
            icon={<HomeIcon fontSize="small" />}
          />
          <StyledBreadcrumb component="a" href="#" label="Gestion" />
          <StyledBreadcrumb
            label="ADMINISTRATION MFA"
            deleteIcon={<ExpandMoreIcon />}
          />
        </Breadcrumbs>
      </Box>

      {/* -------- Barre d'actions, style charte (voir ClientPhysique) -------- */}
      <div className="bG">
        <legend><Typography color="darkgreen">Actions</Typography></legend>
        <Space direction="vertical" style={{ width: '100%' }}>
          <AntButton
            size="large" className="bt_add" type="ghost" htmlType="button" block
            disabled={actionMode === 'view'}
            onClick={resetAction}
          >
            Consulter
          </AntButton>
          {currentTab.supportsAdd && (
            <AntButton
              size="large" className="bt_add" type="ghost" htmlType="button" block
              disabled={actionMode === 'add'}
              onClick={() => setActionMode('add')}
            >
              Ajouter
            </AntButton>
          )}
          {currentTab.supportsEdit && (
            <AntButton
              size="large" className="bt_add" type="ghost" htmlType="button" block
              disabled={actionMode === 'edit' || !hasSelectionToEdit}
              onClick={editSelection}
            >
              Modifier
            </AntButton>
          )}
          <AntButton
            size="large" className="bt_add" type="ghost" htmlType="button" block
            disabled={!isEditing}
            onClick={() => setSaveRequest((value) => value + 1)}
          >
            Enregistrer
          </AntButton>

          <AntButton
            size="large" className="bt_add" type="ghost" htmlType="button" block
            disabled={!isEditing}
            onClick={resetAction}
          >
            Annuler
          </AntButton>
        </Space>
      </div>

      {/* -------- Colonne principale : onglets + panneau actif -------- */}
      <div className="clientP">
        <AntTabs
          style={{ overflowX: 'scroll', msOverflowY: 'scroll' }}
          size="large"
          type="card"
          activeKey={String(tab)}
          onChange={(key) => {
            setTab(Number(key));
            resetAction();
          }}
          items={items}
        />
      </div>
    </div>
  );
}
